"use client";

import React, { useEffect } from "react";
import Link from "next/link";
import { RefreshCw, Home, ShieldAlert } from "lucide-react";

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    console.error("Global application error:", error);
  }, [error]);

  return (
    <html lang="en">
      <body className="flex min-h-screen flex-col items-center justify-center bg-slate-50 p-6 font-sans text-slate-900 antialiased">
        <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-lg">
          <div className="mx-auto mb-4 flex size-14 items-center justify-center rounded-2xl bg-red-50 text-red-600 shadow-inner">
            <ShieldAlert className="size-7" aria-hidden="true" />
          </div>

          <h1 className="text-xl font-bold text-slate-900">Application Error</h1>
          <p className="mt-2 text-sm text-slate-600">
            A critical error occurred while rendering the page. You can reload the application or return to the homepage.
          </p>

          {error?.digest && (
            <div className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-slate-100 px-2.5 py-1 text-[11px] font-mono text-slate-500">
              <span>Digest:</span>
              <span className="font-semibold text-slate-700">{error.digest}</span>
            </div>
          )}

          <div className="mt-6 flex flex-col gap-2.5 sm:flex-row sm:justify-center">
            <button
              type="button"
              onClick={() => {
                if (typeof window !== "undefined") {
                  window.location.reload();
                } else {
                  reset();
                }
              }}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#0f9a84] px-4 py-2.5 text-sm font-bold text-white shadow-xs transition-colors hover:bg-[#0d8572] cursor-pointer"
            >
              <RefreshCw className="size-4" aria-hidden="true" />
              Reload application
            </button>
            <Link
              href="/"
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 shadow-2xs transition-colors hover:bg-slate-50"
            >
              <Home className="size-4" aria-hidden="true" />
              Return home
            </Link>
          </div>
        </div>
      </body>
    </html>
  );
}
